/**
 * A list of instruments that can be typed into: "vio" leaves the violin, the viola and the
 * violoncello, "brass" leaves the brass.
 *
 * Every place that asks for an instrument asks through this -- a roster's next player, the
 * library's "written for", an editor part's instrument -- because a native menu of thirty or
 * fifty names is scrolled through a name at a time, and a picker defined once per place
 * drifts. It is react-select, drawn in the look of whatever it sits on: the dark strip of a
 * board, the paper of a page (`look`, as `ScorePager` has it), or a form on dark chrome, whose
 * fields are a finger tall where a strip's are a pointer's.
 *
 * `value` of `null` makes it an action rather than a setting -- "+ Player" -- which says what
 * it does until something is picked, and is empty again once it is.
 *
 * The menu is drawn on the element on the whole screen, or else on the page's body, so a
 * toolbar or a panel that clips what overflows it does not cut the list off.
 */

import Select from "react-select";
import type { ClassNamesConfig, FilterOptionOption, GroupBase } from "react-select";
import type { Seat } from "@app/shared-types";

export interface Pick {
  readonly value: string;
  readonly label: string;
  /** The heading it is listed under, which a search matches too; none lists it on its own. */
  readonly group?: string | null;
}

export type Look = "shell" | "paper" | "form";

export interface InstrumentPickerProps {
  readonly options: readonly Pick[];
  /** The one chosen, or `null` for a picker that does something and forgets it. */
  readonly value: string | null;
  readonly placeholder: string;
  onPick(value: string): void;
  readonly look: Look;
  readonly label: string;
  readonly title?: string;
  /** Its width, in the caller's terms. */
  readonly className?: string;
}

type Groups = GroupBase<Pick>;

const LOOKS: Readonly<Record<Look, ClassNamesConfig<Pick, false, Groups>>> = {
  form: {
    control: ({ isFocused }) =>
      `h-11 rounded-md border bg-shell-key px-2.5 text-[14px] text-shell-lit hover:bg-shell-hot ${
        isFocused ? "border-shell-live" : "border-shell-seam"
      }`,
    placeholder: () => "text-shell-lit",
    input: () => "text-shell-lit",
    dropdownIndicator: () => "pl-1 text-shell-dim",
    menu: () =>
      "mt-1 min-w-[13rem] rounded-md border border-shell-edge bg-shell-bar p-1 text-[14px] shadow-sheet",
    groupHeading: () =>
      "px-2.5 pb-0.5 pt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-shell-dim",
    option: ({ isFocused, isSelected }) =>
      `rounded px-2.5 py-2 ${isFocused ? "bg-shell-hot" : ""} ${
        isSelected ? "text-shell-live" : "text-shell-lit"
      }`,
    noOptionsMessage: () => "px-2.5 py-2 text-shell-dim",
  },
  shell: {
    control: ({ isFocused }) =>
      `h-7 rounded bg-shell-key px-1.5 text-[11px] text-shell-lit hover:bg-shell-hot ${
        isFocused ? "ring-1 ring-shell-live" : ""
      }`,
    placeholder: () => "text-shell-lit",
    input: () => "text-shell-lit",
    dropdownIndicator: () => "pl-1 text-shell-dim",
    menu: () =>
      "mt-1 min-w-[13rem] rounded-md border border-shell-edge bg-shell-bar p-1 text-[12px] shadow-sheet",
    groupHeading: () =>
      "px-2.5 pb-0.5 pt-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-shell-dim",
    option: ({ isFocused, isSelected }) =>
      `rounded px-2.5 py-1 ${isFocused ? "bg-shell-hot" : ""} ${
        isSelected ? "text-shell-live" : "text-shell-lit"
      }`,
    noOptionsMessage: () => "px-2.5 py-1.5 text-shell-dim",
  },
  paper: {
    control: ({ isFocused }) =>
      `h-10 rounded-[10px] border bg-leaf px-2.5 text-[13px] text-ink ${
        isFocused ? "border-ink" : "border-rule"
      }`,
    placeholder: () => "text-ink-faded",
    input: () => "text-ink",
    dropdownIndicator: () => "pl-1 text-ink-quiet",
    menu: () =>
      "mt-1 min-w-[13rem] rounded-[10px] border border-rule bg-leaf p-1 text-[13px] shadow-modal",
    groupHeading: () =>
      "px-2.5 pb-0.5 pt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-quiet",
    option: ({ isFocused, isSelected }) =>
      `rounded-[6px] px-2.5 py-1 ${isFocused ? "bg-track" : ""} ${
        isSelected ? "font-medium text-ink" : "text-ink-faded"
      }`,
    noOptionsMessage: () => "px-2.5 py-1.5 text-ink-quiet",
  },
};

/** Seats as picks, each under its family, named in words and not by its identifier. */
export function fromSeats(seats: readonly Seat[]): Pick[] {
  return seats.map((seat) => ({
    value: seat.instrument,
    label: seat.label,
    group: seat.family?.replace(/_/g, " ") ?? null,
  }));
}

/** What the list is made of: headed groups in the order they first appear, where it has any. */
function grouped(options: readonly Pick[]): Array<Pick | Groups> {
  if (options.every((option) => (option.group ?? null) === null)) return [...options];
  const out: Array<Pick | Groups> = [];
  const heads = new Map<string, Pick[]>();
  for (const option of options) {
    const group = option.group ?? null;
    if (group === null) {
      out.push(option);
      continue;
    }
    let within = heads.get(group);
    if (within === undefined) {
      within = [];
      heads.set(group, within);
      out.push({ label: group, options: within });
    }
    within.push(option);
  }
  return out;
}

/** Whether what is typed is in the name or in the heading it is listed under. */
function matches(candidate: FilterOptionOption<Pick>, typed: string): boolean {
  const needle = typed.trim().toLowerCase();
  if (needle === "") return true;
  const { label, group } = candidate.data;
  return label.toLowerCase().includes(needle) || (group ?? "").toLowerCase().includes(needle);
}

export default function InstrumentPicker({
  options,
  value,
  placeholder,
  onPick,
  look,
  label,
  title,
  className,
}: InstrumentPickerProps): JSX.Element {
  const chosen = value === null ? null : (options.find((one) => one.value === value) ?? null);
  const screen = document.fullscreenElement;

  return (
    <div title={title} className={className}>
      <Select<Pick, false, Groups>
        unstyled
        classNames={LOOKS[look]}
        // Its own 38px floor would outgrow a toolbar's 28px controls; the look sets the height.
        styles={{
          control: (base) => ({ ...base, minHeight: undefined }),
          menuPortal: (base) => ({ ...base, zIndex: 50 }),
        }}
        options={grouped(options)}
        value={chosen}
        placeholder={placeholder}
        onChange={(picked) => {
          if (picked !== null) onPick(picked.value);
        }}
        filterOption={matches}
        aria-label={label}
        noOptionsMessage={() => "No instrument by that name"}
        menuPortalTarget={screen instanceof HTMLElement ? screen : document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        components={{ IndicatorSeparator: null }}
      />
    </div>
  );
}
